import {
	AddOnItem,
	BillingCycle,
	Env,
	ScheduledChange,
	SubscriptionProvider,
	UserSubscriptionStatus,
	dateOrNull,
} from "../../../index";

interface UserSubscriptionProps {
	internalId: string;
	externalId: string;
	status: UserSubscriptionStatus;
	createdAt: Date | null;
	updatedAt: Date | null;
	firstBilledAt: Date | null;
	nextBilledAt: Date | null;
	billingCycle: BillingCycle;
	scheduledChange: ScheduledChange | null;
	currentBillingPeriodStart: Date | null;
	currentBillingPeriodEnd: Date | null;
	subscriptionProvider: SubscriptionProvider;
	items: AddOnItem[];
}

class UserSubscription {
	constructor(public _: UserSubscriptionProps) {}

	static fromFirestore(userSubscription: any) {
		const data = userSubscription.data();
		return new UserSubscription({
			internalId: userSubscription.id,
			externalId: data.external_id,
			status: data.status,
			createdAt: dateOrNull(data.created_at),
			updatedAt: dateOrNull(data.updated_at),
			firstBilledAt: dateOrNull(data.first_billed_at),
			nextBilledAt: dateOrNull(data.next_billed_at),
			billingCycle: BillingCycle.fromJson(data?.billing_cycle),
			scheduledChange:
				data?.scheduled_change &&
				ScheduledChange.fromJson(data.scheduled_change),
			currentBillingPeriodStart: dateOrNull(data?.current_billing_period_start),
			currentBillingPeriodEnd: dateOrNull(data?.current_billing_period_end),
			subscriptionProvider: SubscriptionProvider.fromId(
				data.subscription_provider
			),
			items: data?.items?.map((item: any) => AddOnItem.fromJson(item)) || [],
		});
	}

	static fromJson(data: any) {
		return new UserSubscription({
			internalId: data.internal_id,
			externalId: data.external_id,
			status: data.status,
			createdAt: dateOrNull(data.created_at),
			updatedAt: dateOrNull(data.updated_at),
			firstBilledAt: dateOrNull(data.first_billed_at),
			nextBilledAt: dateOrNull(data.next_billed_at),
			billingCycle: BillingCycle.fromJson(data.billing_cycle),
			scheduledChange:
				data.scheduled_change &&
				ScheduledChange.fromJson(data.scheduled_change),
			currentBillingPeriodStart: dateOrNull(data.current_billing_period_start),
			currentBillingPeriodEnd: dateOrNull(data.current_billing_period_end),
			subscriptionProvider: SubscriptionProvider.fromId(
				data.subscription_provider
			),
			items: data.items?.map((item: any) => AddOnItem.fromJson(item)) || [],
		});
	}

	static fromPaddleData(internalId: string, data: any, env: Env) {
		return new UserSubscription({
			internalId: internalId,
			externalId: data.id,
			status: data.status,
			createdAt: dateOrNull(data.created_at),
			updatedAt: dateOrNull(data.updated_at),
			firstBilledAt: dateOrNull(data.first_billed_at),
			nextBilledAt: dateOrNull(data.next_billed_at),
			billingCycle: BillingCycle.fromJson(data.billing_cycle),
			scheduledChange:
				data.scheduled_change &&
				ScheduledChange.fromJson(data.scheduled_change),
			currentBillingPeriodStart: dateOrNull(
				data.current_billing_period?.starts_at
			),
			currentBillingPeriodEnd: dateOrNull(data.current_billing_period?.ends_at),
			subscriptionProvider: SubscriptionProvider.paddle,
			items: data?.items?.map((item: any) => AddOnItem.fromPaddleData(item, env)) || [],
		});
	}

	copyWith(props: Partial<UserSubscriptionProps>) {
		return new UserSubscription({ ...this._, ...props });
	}

	isArchived() {
		return this._.status === UserSubscriptionStatus.CANCELED;
	}

	toJson(withInternalId = true) {
		const data = {
			external_id: this._.externalId,
			status: this._.status,
			created_at: this._.createdAt,
			updated_at: this._.updatedAt,
			first_billed_at: this._.firstBilledAt,
			next_billed_at: this._.nextBilledAt,
			billing_cycle: this._.billingCycle?.toJson(),
			scheduled_change: this._.scheduledChange?.toJson(),
			current_billing_period_start: this._.currentBillingPeriodStart,
			current_billing_period_end: this._.currentBillingPeriodEnd,
			subscription_provider: this._.subscriptionProvider._.id,
			items: this._.items?.map((item) => item.toJson()) || [],
		} as any;
		if (withInternalId) {
			data["internal_id"] = this._.internalId;
		}
		return data;
	}
}

export { UserSubscription };
